import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Avatar, Box, Button, Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import SpaRoundedIcon     from '@mui/icons-material/SpaRounded';
import MenuRoundedIcon    from '@mui/icons-material/MenuRounded';
import LogoutRoundedIcon  from '@mui/icons-material/LogoutRounded';
import { V4ThemeProvider } from '../v4/theme';
import { RequireAuth, useShell } from '../v4/shell';
import GrowSetupPicker  from '../v4/components/GrowSetupPicker';
import ColorModeToggle  from '../v4/components/ColorModeToggle';
import ThemePicker      from '../v4/components/ThemePicker';
import { V5_VARIANT } from './theme';

const SERIF = '"Fraunces","Georgia",serif';
const SIDEBAR_W = 256;

function greeting() {
  const h = new Date().getHours();
  if (h < 5)  return 'Gute Nacht';
  if (h < 11) return 'Guten Morgen';
  if (h < 17) return 'Guten Tag';
  return 'Guten Abend';
}

/* Leaf mark drawn with two rounded petals */
function LeafMark({ size = 40 }) {
  return (
    <Box sx={{
      width: size, height: size, borderRadius: '50% 8px 50% 8px',
      background: 'linear-gradient(145deg,var(--v4-cyan-light),var(--v4-cyan-deep))',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 6px 18px rgba(var(--v4-cyan-rgb),0.3)',
      flexShrink: 0,
    }}>
      <SpaRoundedIcon sx={{ color: '#fff', fontSize: size * 0.5 }} />
    </Box>
  );
}

function SidebarContent({ onNavigate }) {
  const shell = useShell();

  return (
    <Stack sx={{ height: '100%', p: 2.5, gap: 2.5 }}>
      {/* Brand */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 0.5 }}>
        <LeafMark />
        <Box>
          <Typography sx={{ fontFamily: SERIF, fontWeight: 700, fontSize: '1.35rem', color: 'var(--v4-text)', lineHeight: 1 }}>
            EcoScope
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(var(--v4-muted-rgb),0.8)', fontStyle: 'italic', fontFamily: SERIF }}>
            Gewächshaus
          </Typography>
        </Box>
      </Stack>

      <GrowSetupPicker />

      {/* Nav */}
      <Stack spacing={0.5} sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(var(--v4-muted-rgb),0.6)', px: 1.5, mb: 0.5 }}>
          Garten
        </Typography>
        {shell.items.map(({ label, icon: Icon, path }) => {
          const active = shell.isActive(path);
          return (
            <Box
              key={path}
              onClick={() => { shell.go(path); onNavigate?.(); }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.1, borderRadius: '14px', cursor: 'pointer',
                color: active ? 'var(--v4-cyan-deep)' : 'rgba(var(--v4-text-rgb),0.72)',
                background: active ? 'rgba(var(--v4-cyan-rgb),0.13)' : 'transparent',
                fontWeight: active ? 700 : 500,
                transition: 'background .2s ease, color .2s ease, transform .2s ease',
                '&:hover': { background: 'rgba(var(--v4-cyan-rgb),0.08)', transform: 'translateX(3px)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: '10px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: active ? 'var(--v4-cyan)' : 'rgba(var(--v4-fg-rgb),0.05)',
                color: active ? '#fff' : 'inherit',
                transition: 'background .2s ease',
              }}>
                <Icon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 'inherit', color: 'inherit' }}>{label}</Typography>
            </Box>
          );
        })}
      </Stack>

      {/* Unsaved changes */}
      {shell.unsavedCount > 0 && (
        <Box sx={{ p: 1.75, borderRadius: '16px', background: 'rgba(var(--v4-orange-rgb),0.1)', border: '1px solid rgba(var(--v4-orange-rgb),0.3)' }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--v4-orange)' }}>
            {shell.unsavedCount} ungespeicherte Änderung{shell.unsavedCount > 1 ? 'en' : ''}
          </Typography>
          <Button size="small" variant="contained" fullWidth sx={{ mt: 1 }} onClick={shell.saveConfig}>
            Speichern
          </Button>
        </Box>
      )}

      {/* User */}
      <Stack direction="row" alignItems="center" spacing={1.25}
        sx={{ p: 1.25, borderRadius: '16px', background: 'rgba(var(--v4-fg-rgb),0.04)' }}>
        <Avatar src={shell.avatar} sx={{ width: 36, height: 36, cursor: 'pointer' }} onClick={() => shell.go('/settings')} />
        <Typography noWrap sx={{ flex: 1, fontSize: '0.85rem', fontWeight: 600, color: 'var(--v4-text)' }}>
          {shell.username || 'Gärtner:in'}
        </Typography>
        <ThemePicker sx={{ border: 'none' }} />
        <ColorModeToggle sx={{ border: 'none' }} />
        <Tooltip title="Logout">
          <IconButton size="small" onClick={shell.logout} sx={{ color: 'rgba(var(--v4-muted-rgb),0.7)', '&:hover': { color: 'var(--v4-red)' } }}>
            <LogoutRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

function PageHeader({ onMenu }) {
  const shell = useShell();
  const [now, setNow] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(id); }, []);

  return (
    <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ mb: 3.5, gap: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <IconButton onClick={onMenu} sx={{ display: { md: 'none' }, color: 'var(--v4-text)' }}>
          <MenuRoundedIcon />
        </IconButton>
        <Box>
          <Typography sx={{ fontSize: '0.85rem', color: 'rgba(var(--v4-muted-rgb),0.85)', fontStyle: 'italic', fontFamily: SERIF }}>
            {greeting()}{shell.username ? `, ${shell.username}` : ''}
          </Typography>
          <Typography sx={{ fontFamily: SERIF, fontWeight: 700, fontSize: { xs: '1.8rem', md: '2.4rem' }, lineHeight: 1.1, color: 'var(--v4-text)', letterSpacing: '-0.01em' }}>
            {shell.current?.label ?? 'EcoScope'}
          </Typography>
        </Box>
      </Stack>
      <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '0.85rem', color: 'rgba(var(--v4-muted-rgb),0.8)', textAlign: 'right' }}>
        {now.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}
      </Typography>
    </Stack>
  );
}

function Shell() {
  const [drawer, setDrawer] = useState(false);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', position: 'relative' }}>
      {/* Soft botanical backdrop */}
      <Box sx={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, opacity: 'var(--v4-glow-k)',
        background: `
          radial-gradient(900px 500px at 100% 0%, rgba(var(--v4-cyan-rgb),0.10), transparent 60%),
          radial-gradient(700px 500px at 0% 100%, rgba(var(--v4-orange-rgb),0.08), transparent 60%)`,
      }} />

      {/* Desktop sidebar */}
      <Box component="aside" sx={{
        display: { xs: 'none', md: 'block' },
        width: SIDEBAR_W, flexShrink: 0,
        position: 'sticky', top: 0, height: '100vh',
        borderRight: '1px solid rgba(var(--v4-fg-rgb),0.07)',
        background: 'rgba(var(--v4-surface-rgb),0.7)',
        backdropFilter: 'blur(12px)',
        zIndex: 2,
      }}>
        <SidebarContent />
      </Box>

      {/* Mobile drawer */}
      <Drawer open={drawer} onClose={() => setDrawer(false)}
        PaperProps={{ sx: { width: SIDEBAR_W + 24, background: 'var(--v4-bg)', border: 'none', borderRadius: '0 24px 24px 0' } }}>
        <SidebarContent onNavigate={() => setDrawer(false)} />
      </Drawer>

      <Box component="main" sx={{ flex: 1, minWidth: 0, px: { xs: 2, sm: 3, md: 5 }, py: { xs: 2, md: 4 }, position: 'relative', zIndex: 1 }}>
        <PageHeader onMenu={() => setDrawer(true)} />
        <Outlet />
      </Box>
    </Box>
  );
}

export default function V5Layout() {
  return (
    <V4ThemeProvider variant={V5_VARIANT}>
      <RequireAuth>
        <Shell />
      </RequireAuth>
    </V4ThemeProvider>
  );
}
